import React from 'react'
import {BrowserRouter as Router} from 'react-router-dom'
import {createRoot} from 'react-dom/client'
import { AuthtoolProvider, init } from '@mia-platform-internal/authtool-client-js/react'

import App from './App'
import './index.css'
import {loadHubConfig} from './config'
import ThemeProvider from './ThemeProvider'

// Apply dark mode on initial load if saved in localStorage
const applyInitialDarkMode = () => {
  const savedTheme = localStorage.getItem('theme') || 'dark';
  const savedAccent = localStorage.getItem('accentColor') || '#3B82F6';

  // Remove all theme classes first
  document.documentElement.classList.remove('dark-mode', 'theme-noctis', 'theme-github', 'theme-synthwave', 'theme-hackerx-green', 'theme-hackerx-ocean', 'theme-monokai', 'theme-catppuccin-latte', 'theme-catppuccin-frappe', 'theme-catppuccin-macchiato', 'theme-catppuccin-mocha', 'theme-high-contrast', 'theme-light-high-contrast');
  document.body.classList.remove('dark-mode', 'theme-noctis', 'theme-github', 'theme-synthwave', 'theme-hackerx-green', 'theme-hackerx-ocean', 'theme-monokai', 'theme-catppuccin-latte', 'theme-catppuccin-frappe', 'theme-catppuccin-macchiato', 'theme-catppuccin-mocha', 'theme-high-contrast', 'theme-light-high-contrast');
  
  // Apply saved theme
  if (savedTheme === 'light' || savedTheme === 'monokai' || savedTheme === 'catppuccin-latte' || savedTheme === 'light-high-contrast') {
    // Light themes
    if (savedTheme === 'light-high-contrast') {
      document.documentElement.classList.add('theme-light-high-contrast');
      document.body.classList.add('theme-light-high-contrast');
    } else if (savedTheme !== 'light') {
      document.documentElement.classList.add(`theme-${savedTheme}`);
      document.body.classList.add(`theme-${savedTheme}`);
    }
  } else {
    // Dark themes
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark-mode');
      document.body.classList.add('dark-mode');
    } else if (savedTheme === 'high-contrast') {
      document.documentElement.classList.add('dark-mode', 'theme-high-contrast');
      document.body.classList.add('dark-mode', 'theme-high-contrast');
    } else {
      document.documentElement.classList.add('dark-mode', `theme-${savedTheme}`);
      document.body.classList.add('dark-mode', `theme-${savedTheme}`);
    }
  }
  
  // Apply saved accent color and font size
  document.documentElement.style.setProperty('--accent-color', savedAccent);
  document.documentElement.style.setProperty('--accent-primary', savedAccent);
  document.documentElement.style.setProperty('--palette-action-primary-default', savedAccent);
  document.documentElement.style.setProperty('--palette-action-link-default', savedAccent);
  document.documentElement.style.setProperty('--mia-purple-primary', savedAccent);
};

// Apply dark mode immediately (before React renders)
applyInitialDarkMode();

// Suppress harmless ResizeObserver loop errors and React defaultProps warnings
const resizeObserverLoopErrRe = /ResizeObserver loop/;
const defaultPropsWarningRe = /Support for defaultProps will be removed from function components/;
const originalError = window.console.error;
window.console.error = (...args) => {
  const firstArg = args[0];
  if (firstArg) {
    // Check if it's a string or Error object with ResizeObserver message
    const message = typeof firstArg === 'string' ? firstArg : firstArg.message || '';
    if (resizeObserverLoopErrRe.test(message) || defaultPropsWarningRe.test(message)) {
      return;
    }
  }
  originalError.apply(window.console, args);
};

const rootComponent = () => {
  const basename = import.meta.env.BASE_URL
  const authtoolBaseUrl = '/bff'
  
  return (
    <AuthtoolProvider value={init({baseUrl: authtoolBaseUrl})}>
      <Router 
        basename={basename}
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </Router>
    </AuthtoolProvider>
  )
}

let root

function retrieveContainer (props) {
  const {container} = props
  return container ? container.querySelector('#root') : document.querySelector('#root')
}

function render (props) {
  root = root || createRoot(retrieveContainer(props))
  
  loadHubConfig().then(() => {
    root.render(rootComponent())
  })
}

render({})
